<template>
  <component
    :is="tag"
    v-bind="bindings"
    class="bui-nav-item"
    :class="{ active: isActive, narrow, 'has-status': statusBg }"
    :title="narrow ? label : undefined"
    :data-testid="resolvedTestId"
    @click="onClick"
  >
    <NavIco v-if="slots.icon" :size="navIcoSize">
      <slot name="icon" />
    </NavIco>
    <span v-if="!narrow" class="label">{{ label }}</span>
  </component>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import NavIco from '../../atoms/NavIco/NavIco.vue';
import useIsMobile from '@/composables/utils/isMobile';

const slots = useSlots();
const isMobile = useIsMobile();
const navIcoSize = computed(() => (isMobile.value ? 32 : 40));

const props = withDefaults(
  defineProps<{
    label: string;
    to?: string;
    /** When true, render as <button> rather than RouterLink */
    asButton?: boolean;
    narrow?: boolean;
    /** Force-active styling (used by parent for non-routed items) */
    active?: boolean;
    /** Use exact-match route activeness (good for `/`) */
    exact?: boolean;
    /** Optional CSS background for the icon (e.g. status badge fill) */
    statusBg?: string;
    dataTestId?: string;
  }>(),
  { asButton: false, narrow: false, active: false, exact: false },
);

const emit = defineEmits<{ click: [event: MouseEvent] }>();

const route = useRoute();

const tag = computed(() => (props.asButton || !props.to ? 'button' : RouterLink));

const bindings = computed(() => {
  if (props.asButton || !props.to) return { type: 'button' as const };
  return { to: props.to };
});

const resolvedTestId = computed(() => {
  if (props.dataTestId) return props.dataTestId;
  if (!props.to) return undefined;
  const slug = props.to.replace(/^\//, '').replace(/\//g, '-') || 'home';
  return `nav-${slug}`;
});

const isActive = computed(() => {
  if (props.active) return true;
  if (!props.to) return false;
  if (props.exact) return route.path === props.to;
  return route.path === props.to || route.path.startsWith(props.to + '/');
});

function onClick(e: MouseEvent) {
  emit('click', e);
}
</script>

<style lang="scss" scoped>
.bui-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: var(--nav-padding-box, 8px);
  background: transparent;
  border: 0;
  border-radius: var(--nav-radius-box, 12px);
  color: var(--nav-label-primary, #f6f7ff);
  text-decoration: none;
  width: 100%;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--nav-item-font-size, var(--type-body-lg-size, 20px));
  line-height: var(--nav-item-line-height, var(--type-body-lg-line-height, 23px));
  text-align: left;

  &:hover {
    background: var(--nav-btn-hover, #8e3fbc);
  }

  &.active {
    background: var(--nav-btn-active, #a420c8);

    /* inner_radius = outer_radius - gap (concentric corners on status chip vs row) */
    :deep(.bui-nav-status-ico) {
      border-radius: max(
        0px,
        calc(
          var(--nav-radius-box, 12px) - var(--nav-status-inner-gap, 4px)
        )
      );
    }
  }

  &.narrow {
    width: var(--nav-item-narrow-w, 56px);
    padding: var(--nav-padding-box, 8px) 0;
    justify-content: center;
    gap: 0;
  }

  &:focus-visible {
    outline: 2px solid var(--nav-label-primary, #f6f7ff);
    outline-offset: 2px;
  }

  .label {
    white-space: nowrap;
    text-overflow: ellipsis;
    /* Ellipsis needs hidden on x only; full overflow:hidden clips descenders. */
    overflow-x: hidden;
    overflow-y: visible;
    flex: 1 0 0;
    min-width: 0;
  }
}
</style>
